import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Truck, Tag, Search, TrendingDown, Users, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { CatalogAPI, resolveImage, SiteConfigAPI } from '../lib/api';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useToast } from '../hooks/use-toast';

const Hero = ({ editMode = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState({ products: 0, suppliers: 0, brands: 0 });
  const navigate = useNavigate();
  const { config, refresh } = useSiteConfig();
  const { toast } = useToast();

  const saveField = async (key, value) => {
    try {
      const full = await SiteConfigAPI.adminGet();
      full[key] = value;
      await SiteConfigAPI.adminSet(full);
      await refresh();
      toast({ title: 'Modifié' });
    } catch (e) { toast({ title: 'Erreur' }); }
  };

  const uploadHeroImg = async (file) => {
    if (!file) return;
    try {
      const res = await SiteConfigAPI.adminGet();
      const up = await (await import('../lib/api')).AdminAPI.upload(file);
      const url = up.url || (up.file_id ? `/api/files/${up.file_id}` : '');
      res['home.hero.image'] = url;
      await SiteConfigAPI.adminSet(res);
      await refresh();
      toast({ title: 'Image mise à jour' });
    } catch (e) { toast({ title: 'Erreur', description: 'Échec image' }); }
  };
  const heroTitle = config['home.hero.title'];
  const heroSubtitle = config['home.hero.subtitle'];
  const heroImage = config['home.hero.image'];
  const heroCtaLabel = config['home.hero.cta_label'];
  const heroCtaUrl = config['home.hero.cta_url'];

  useEffect(() => {
    Promise.all([
      CatalogAPI.products({ limit: 200 }).catch(() => []),
      CatalogAPI.suppliers().catch(() => []),
      CatalogAPI.brands().catch(() => []),
    ]).then(([products, suppliers, brands]) => {
      setStats({ products: products.length, suppliers: suppliers.length, brands: brands.length });
    });
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/recherche?q=${encodeURIComponent(query)}`);
  };

  return (
    <section className="bg-gradient-to-b from-[#FAF7F2] to-white">
      <div className="max-w-7xl mx-auto px-4 py-8 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main hero card */}
          <div className="lg:col-span-2 bg-gradient-to-br from-[#1F4D3F] to-[#173B30] rounded-2xl p-8 lg:p-12 relative overflow-hidden text-white">
            {heroImage && (
              <img
                src={resolveImage(heroImage)}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
                onError={e => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="relative z-10 max-w-xl">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/15 backdrop-blur text-white text-xs font-medium rounded-full mb-4 border border-white/20">
                <TrendingDown className="w-3.5 h-3.5" /> {t('hero.badge')}
              </span>
              {editMode && (
                <label className="inline-flex items-center gap-1 mb-3 bg-white/90 text-[#1F4D3F] text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:bg-white">
                  Changer l'image de fond
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadHeroImg(e.target.files[0])} />
                </label>
              )}
              {editMode ? (
                <textarea defaultValue={heroTitle} onBlur={(e) => e.target.value !== heroTitle && saveField('home.hero.title', e.target.value)}
                  className="font-serif text-4xl lg:text-5xl font-semibold leading-tight bg-white/15 rounded-lg p-2 w-full text-white resize-none" rows={2} />
              ) : (
                <h1 className="font-serif text-4xl lg:text-5xl font-semibold leading-tight">{heroTitle}</h1>
              )}
              {editMode ? (
                <textarea defaultValue={heroSubtitle} onBlur={(e) => e.target.value !== heroSubtitle && saveField('home.hero.subtitle', e.target.value)}
                  className="mt-4 text-base lg:text-lg bg-white/15 rounded-lg p-2 w-full text-white resize-none" rows={2} />
              ) : (
                <p className="mt-4 text-white/85 text-base lg:text-lg">{heroSubtitle}</p>
              )}

              <form onSubmit={onSearch} className="mt-6 flex bg-white rounded-lg overflow-hidden shadow-lg">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#7A6F5C]" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('hero.search_placeholder')}
                    className="pl-11 h-12 border-0 bg-white text-[#2D2A26] focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
                <Button type="submit" className="h-12 bg-[#C75B27] hover:bg-[#A84A1F] text-white px-6 rounded-none">
                  {heroCtaLabel || t('hero.cta')}
                </Button>
              </form>

              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
                <span className="inline-flex items-center gap-1.5"><Package className="w-4 h-4" /> {t('hero.stats_products', { count: stats.products || 6000 })}</span>
                <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {t('hero.stats_suppliers', { count: stats.suppliers || 50 })}</span>
                <span className="inline-flex items-center gap-1.5"><Tag className="w-4 h-4" /> {t('hero.stats_brands', { count: stats.brands || 20 })}</span>
              </div>
            </div>

            {/* Decorative bg shapes */}
            <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-white/5" />
            <div className="absolute right-16 top-12 w-32 h-32 rounded-full bg-[#E0A52E]/10 hidden lg:block" />
            <div className="absolute right-8 bottom-16 w-20 h-20 rounded-full bg-[#C75B27]/15 hidden lg:block" />
          </div>

          {/* Side cards */}
          <div className="flex flex-col gap-4">
            <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 flex items-center gap-4 hover:border-[#1F4D3F] transition">
              <div className="w-12 h-12 rounded-full bg-[#F5F1EA] flex items-center justify-center shrink-0">
                <TrendingDown className="w-6 h-6 text-[#C75B27]" />
              </div>
              <div>
                <div className="font-semibold text-[#2D2A26]">{t('hero.savings_title')}</div>
                <div className="text-sm text-[#7A6F5C]">{t('hero.savings_desc')}</div>
              </div>
            </div>
            <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 flex items-center gap-4 hover:border-[#1F4D3F] transition">
              <div className="w-12 h-12 rounded-full bg-[#F5F1EA] flex items-center justify-center shrink-0">
                <Truck className="w-6 h-6 text-[#1F4D3F]" />
              </div>
              <div>
                <div className="font-semibold text-[#2D2A26]">{t('hero.delivery_title')}</div>
                <div className="text-sm text-[#7A6F5C]">{t('hero.delivery_desc')}</div>
              </div>
            </div>
            <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 flex items-center gap-4 hover:border-[#1F4D3F] transition">
              <div className="w-12 h-12 rounded-full bg-[#F5F1EA] flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-[#1F4D3F]" />
              </div>
              <div>
                <div className="font-semibold text-[#2D2A26]">{t('hero.verified_title')}</div>
                <div className="text-sm text-[#7A6F5C]">{t('hero.verified_desc')}</div>
              </div>
            </div>
            <Link to="/inscription?role=supplier" className="bg-[#2D2A26] hover:bg-[#1F1D1A] text-white rounded-2xl p-5 flex items-center justify-between gap-2 transition group">
              <div>
                <div className="font-semibold">{t('hero.supplier_card_title')}</div>
                <div className="text-sm text-white/70">{t('hero.supplier_card_desc')}</div>
              </div>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
